import React, { useEffect, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import useDebounce from '@/Hooks/useDebounce';
import { BetaFeatureRequest, BetaFeatureResponse } from '@/Interfaces/I_betaFeature';
import { UserGroupResponse, UserResponse } from '@/Interfaces/I_userGroup';

import {
  betaFeatureSearchUserGroups,
  betaFeatureSearchUsers,
  deleteBetaFeature,
  getBetaFeaturesData,
  postCreateBetaFeatue,
  putUpdateBetaFeatue,
} from '@/Redux/slices/BetaFeaturesSlice';

import { timeTranslate } from '@/Utils/timeTranslate';

const { Column } = Table;

const BetaFeatures = () => {
  // Form and Redux setup
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { betaFeatures } = useAppSelector((state) => state.betaFeatures.value);
  const loading = useAppSelector((state) => state.betaFeatures.value.betaFeatures.loading);

  // State management
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);

  const [users, setUsers] = useState<UserResponse[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroupResponse[]>([]);
  const [searchUserString, setSearchUserString] = useState('');
  const [searchUserGroupString, setSearchUserGroupString] = useState('');

  const handleAddFeature = () => {
    form.resetFields();
    setEditingKey(null);
    setModalVisible(true);
  };

  const handleEdit = (record: BetaFeatureResponse) => {
    form.setFieldsValue({
      ...record,
      expiryTime: dayjs(record.expiryTime),
      users: record.users.map((u) => ({
        value: u.id,
        label: u.name,
      })),
      userGroups: record.userGroups.map((u) => ({
        value: u.id,
        label: u.name,
      })),
    });
    setUsers(record.users);
    setUserGroups(record.userGroups);
    setEditingKey(record.id);
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    dispatch(deleteBetaFeature(id));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const featureData: BetaFeatureRequest = {
        featureName: values.featureName,
        expiryTime: values.expiryTime.format(),
        userIds: form.getFieldValue('users')?.map((u: { value: number }) => u.value) || [],
        userGroupIds:
          form.getFieldValue('userGroups')?.map((u: { value: number }) => u.value) || [],
      };

      if (editingKey) {
        dispatch(putUpdateBetaFeatue({ id: editingKey, data: featureData }));
      } else {
        dispatch(postCreateBetaFeatue(featureData));
      }

      setModalVisible(false);
      setEditingKey(null);
      form.resetFields();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingKey(null);
    form.resetFields();
  };

  const handleSearchUsers = (value: string) => {
    setSearchUserString(value);
  };
  useDebounce(
    () => {
      dispatch(betaFeatureSearchUsers(searchUserString));
    },
    500,
    [searchUserString],
  );

  const handleSearchUserGroups = (value: string) => {
    setSearchUserGroupString(value);
  };
  useDebounce(
    () => {
      dispatch(betaFeatureSearchUserGroups(searchUserGroupString));
    },
    500,
    [searchUserGroupString],
  );
  const handleChangeUsers = (value: number[]) => {
    const allUsers = [...new Set([...users, ...(betaFeatures?.searched?.users || [])])];
    const newUsers = allUsers
      .filter((user) => value.includes(user.id))
      .map((user) => ({
        value: user.id,
        label: user.name,
      }));
    setUsers(allUsers);
    form.setFieldsValue({ users: newUsers });
  };

  const handleChangeUserGroups = (value: number[]) => {
    const allUserGroups = [
      ...new Set([...userGroups, ...(betaFeatures?.searched?.userGroups || [])]),
    ];
    const newUserGroups = allUserGroups
      .filter((group) => value.includes(group.id))
      .map((group) => ({
        value: group.id,
        label: group.name,
      }));
    setUserGroups(allUserGroups);
    form.setFieldsValue({ userGroups: newUserGroups });
  };

  // Effects
  useEffect(() => {
    dispatch(getBetaFeaturesData());
  }, [dispatch]);

  // Render functions
  const renderEmptyState = () => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Beta Features" />
  );

  const renderTable = () => (
    <Table
      dataSource={betaFeatures.data}
      loading={loading}
      pagination={{
        total: betaFeatures.data.length,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        defaultPageSize: pageSize,
        defaultCurrent: 1,
        showSizeChanger: true,
        current: currentPage,
        onChange: (page: number, selectPageSize: number) => {
          setCurrentPage(page);
          setPageSize(selectPageSize);
        },
        onShowSizeChange: (_current, size) => {
          setPageSize(size);
        },
      }}
    >
      <Column title="ID" dataIndex="id" key="id" />
      <Column title="Feature Name" dataIndex="featureName" key="featureName" />
      <Column
        title="Expiry Time"
        dataIndex="expiryTime"
        key="expiryTime"
        render={(text: string) => timeTranslate(moment(text).unix(), true)}
      />
      <Column
        width="25vw"
        title="Users"
        dataIndex="users"
        key="users"
        render={(u: UserResponse[]) => (
          <>
            {u.length === 0 && '-'}

            {u.slice(0, 5).map((user) => (
              <Tag key={user.id}>{user.name}</Tag>
            ))}
            {u.length > 5 && `and ${u.length - 5} more`}
          </>
        )}
      />
      <Column
        width="15vw"
        title="User Groups"
        dataIndex="userGroups"
        key="userGroups"
        render={(ug: UserGroupResponse[]) => (
          <>
            {ug.length === 0 && '-'}

            {ug.slice(0, 5).map((group) => (
              <Tag key={group.id}>{group.name}</Tag>
            ))}
            {ug.length > 5 && `and ${ug.length - 5} more`}
          </>
        )}
      />
      <Column
        title="Creation Date"
        dataIndex="creationDate"
        key="creationDate"
        render={(text: string) => timeTranslate(moment(text).unix(), true)}
      />
      <Column
        title="Last Modified"
        dataIndex="lastModifiedDate"
        key="lastModifiedDate"
        render={(text: string) => timeTranslate(moment(text).unix(), true)}
      />
      <Column
        title="Operation"
        key="operation"
        width={120}
        render={(_: unknown, record: BetaFeatureResponse) => (
          <Space>
            <Typography.Link onClick={() => handleEdit(record)}>Edit</Typography.Link>
            <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)}>
              <Typography.Link type="danger">Delete</Typography.Link>
            </Popconfirm>
          </Space>
        )}
      />
    </Table>
  );

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex justify-end pb-2">
        <Button className="mb-1" onClick={handleAddFeature} type="primary" icon={<PlusOutlined />}>
          Add Beta Feature
        </Button>
      </div>
      {!loading && betaFeatures.data?.length === 0 ? renderEmptyState() : renderTable()}

      <Modal
        title={editingKey ? 'Edit Beta Feature' : 'Add Beta Feature'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="featureName"
            label="Feature Name"
            rules={[{ required: true, message: 'Please input feature name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="expiryTime"
            label="Expiry Time"
            rules={[{ required: true, message: 'Please select expiry time!' }]}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Users" name="users">
            <Select
              mode="multiple"
              placeholder="Select users"
              filterOption={false}
              options={(betaFeatures?.searched?.users || []).map((u) => ({
                label: u.name,
                value: u.id,
              }))}
              notFoundContent={
                betaFeatures.searched.loadingUsers ? <Spin size="small" /> : 'No results found'
              }
              onSearch={handleSearchUsers}
              onChange={handleChangeUsers}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="User Groups" name="userGroups">
            <Select
              mode="multiple"
              placeholder="Select user groups"
              filterOption={false}
              options={(betaFeatures?.searched?.userGroups || []).map((u) => ({
                label: u.name,
                value: u.id,
              }))}
              notFoundContent={
                betaFeatures.searched.loadingUserGroups ? <Spin size="small" /> : 'No results found'
              }
              onSearch={handleSearchUserGroups}
              onChange={handleChangeUserGroups}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BetaFeatures;
